import { createPortal } from "react-dom";
import BaseButton from "../../../components/ui/BaseButton";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { ModuleType } from "../../../utils/enum";
import { useEffect, useState } from "react";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import { createCustomerUser } from "../../../services/api/client";
import { useMutation } from "@tanstack/react-query";
import GenerateSignupModal from "./components/GenerateSignupModal";
import { customerLabel } from "../../../utils/helpers";

const CreateEditClientHeader = ({
  isEdit: isEditProp,
  hideTitle = false,
  actionsContainer = null,
  onSave,
  isLoading,
  isDisabled,
  onCancel,
  isEditable,
  setIsEditable,
  ownerId,
  selectedClient,
  viewOnly = false,
}) => {
  const location = useLocation();
  const { getPermissionForModule, authRole } = useAuth();
  const recordLabel = customerLabel(authRole);
  const { hasUpdateAccess, hasWriteAccess } = getPermissionForModule(ModuleType.CLIENT_CUSTOMER);
  const isEdit = isEditProp ?? location.pathname.toLowerCase().includes("edit");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isConfrimGenerateSignupOpen, setIsConfrimGenerateSignupOpen] = useState(false);

  const { parentClientId } = useParams();
  const isParentClientCreate = searchParams.get("isParentClient") === "true";
  const clientTypeLabel = isParentClientCreate
    ? "Parent Customer"
    : parentClientId
      ? "Entity"
      : "Customer";

  const MODE_DIRECT = searchParams.get("mode") === "DIRECT";
  useEffect(() => {
    if (MODE_DIRECT && !viewOnly) {
      setIsEditable(true);
    }
  }, [MODE_DIRECT, viewOnly]);

  const actions = viewOnly ? null : (
    <>
      {!isEditable && isEdit && hasUpdateAccess ? (
        <BaseButton
          variant="contained"
          color="primary"
          onClick={() => setIsEditable(true)}
          className="py-[5px]"
        >
          Edit Details
        </BaseButton>
      ) : (
        <>
          {hasUpdateAccess && isEdit && (
            <>
              <BaseButton
                type="submit"
                variant="contained"
                color="primary"
                onClick={isEdit ? onCancel : () => navigate("/workBoard/clients")}
                className="py-[5px]"
              >
                Cancel
              </BaseButton>
              <BaseButton
                loaderSize="24px"
                isLoading={isLoading}
                disabled={isLoading || isDisabled}
                type="submit"
                variant="contained"
                color="primary"
                onClick={onSave}
                className="py-[5px]"
              >
                Update
              </BaseButton>
            </>
          )}
        </>
      )}
      {hasUpdateAccess && !ownerId && selectedClient?.isParentClient && (
        <BaseButton
          loaderSize="24px"
          isLoading={isLoading}
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => setIsConfrimGenerateSignupOpen(true)}
          className="py-[5px]"
        >
          Generate Signup
        </BaseButton>
      )}
      {hasWriteAccess && !isEdit && (
        <BaseButton
          loaderSize="24px"
          isLoading={isLoading}
          disabled={isLoading || isDisabled}
          type="submit"
          variant="contained"
          color="primary"
          onClick={onSave}
          className="py-[5px]"
        >
          Create {clientTypeLabel}
        </BaseButton>
      )}
    </>
  );

  const generateSignupModal = isConfrimGenerateSignupOpen && (
    <GenerateSignupModal
      open={isConfrimGenerateSignupOpen}
      close={() => setIsConfrimGenerateSignupOpen(false)}
      selectedClient={selectedClient}
    />
  );

  if (actionsContainer) {
    return (
      <>
        {createPortal(actions, actionsContainer)}
        {generateSignupModal}
      </>
    );
  }

  return (
    <div className={`border-b border-zinc-300 bg-white w-full sticky top-0 z-10`}>
      <BaseWrapper>
        <div className="grid grid-cols-7 py-[7px] items-center">
          <div className="sm:col-span-3 col-span-7">
            {!hideTitle && (
              <h3 className="font-light">
                {viewOnly
                  ? selectedClient?.clientName || `${recordLabel} Details`
                  : isEdit
                    ? `${isEditable ? "Edit" : ""} ${recordLabel} Details`
                    : `Create New ${clientTypeLabel}`}
              </h3>
            )}
          </div>
          <div className="sm:col-span-4 col-span-7 flex items-center justify-end gap-3">
            {actions}
          </div>
        </div>
      </BaseWrapper>
      {generateSignupModal}
    </div>
  );
};

export default CreateEditClientHeader;
